import { cn } from '@nexelem/utils';
import { VariantProps, cva } from 'class-variance-authority';
import { PropsWithChildren } from 'react';

const avatarVariants = cva('flex shrink-0 items-center justify-center rounded-full border', {
  variants: {
    size: {
      small: 'size-5 text-s10-sb',
      medium: 'size-7 text-s11-sb',
      large: 'size-10 text-t14-sb',
      jumbo: 'size-16 text-t24-sb',
    },
    color: {
      turquoise: 'border-add-turquoise40 bg-add-turquoise30 text-add-turquoise70',
      cocoa: 'border-add-cocoa40 bg-add-cocoa30 text-add-cocoa70',
      gold: 'border-add-gold40 bg-add-gold30 text-add-gold70',
      periwinkle: 'border-add-periwinkle40 bg-add-periwinkle30 text-add-periwinkle70',
      violet: 'border-border-status-violet bg-surface-status-violet-lighter text-text-status-violet-darker',
      rose: 'border-border-status-rose bg-surface-status-rose-lighter text-text-status-rose-darker',
      orange: 'border-border-status-orange-lighter bg-surface-status-orange-lighter text-text-status-orange-darker',
      brown: 'border-border-status-brown bg-surface-status-brown-lighter text-text-status-brown-darker',
      yellow: 'border-border-status-yellow-lighter bg-surface-status-yellow-lighter text-text-status-yellow-darker',
      green: 'border-border-status-green bg-surface-status-green-lighter text-text-status-green-darker',
      grey: 'border-border-status-grey bg-surface-status-grey-lighter text-text-status-grey-darker',
      placeholder: 'border-border-status-orange-lighter bg-surface-primary text-icon-status-orange-lighter',
    },
  },
});

type AvatarProps = VariantProps<typeof avatarVariants> & {
  firstName?: string;
  lastName?: string;
  className?: string;
};

const AVAILABLE_COLORS = [
  'turquoise',
  'cocoa',
  'gold',
  'periwinkle',
  'violet',
  'rose',
  'orange',
  'brown',
  'yellow',
  'green',
  'grey',
] satisfies AvatarProps['color'][];

export const Avatar = ({
  size = 'small',
  color: specificColor,
  firstName = '',
  lastName = '',
  className,
  children,
}: PropsWithChildren<AvatarProps>) => {
  const color = specificColor ?? getRandomColor(firstName, lastName);
  return (
    <div className={cn(avatarVariants({ size, color }), className)}>
      {children ? (
        children
      ) : (
        <span className="uppercase">
          {firstName[0]}
          {lastName[0]}
        </span>
      )}
    </div>
  );
};

const getRandomColor = (firstName?: string, lastName?: string): AvatarProps['color'] => {
  const firstNameNumber = firstName ? firstName.charCodeAt(0) : 0;
  const lastNameNumber = lastName ? lastName.charCodeAt(0) : 0;
  return AVAILABLE_COLORS[(firstNameNumber + lastNameNumber) % AVAILABLE_COLORS.length];
};
